import React from 'react';
import '@fortawesome/fontawesome-free/css/all.min.css';

const DashboardSidebar = ({ 
  isOpen, 
  onToggle, 
  selectedDateRange, 
  onDateRangeChange,
  recordTypeFilter,
  onRecordTypeChange,
  // Domains
  domainTypeFilter,
  onDomainTypeChange,
  selectedDomain,
  onDomainChange,
  availableDomains,
  // URLs
  urlTypeFilter,
  onUrlTypeChange,
  availableUrls
}) => {
  const handleOverlayClick = () => {
    onToggle();
  };
  
  const dateRangeOptions = [
    { value: 7, label: 'Last 7 Days' },
    { value: 30, label: 'Last 30 Days' },
    { value: 60, label: 'Last 60 Days' },
    { value: 90, label: 'Last 90 Days' },
    { value: 365, label: 'Last Year' },
    { value: 1825, label: 'Last 5 Years' },
  ];

  // Domain type filter options
  const domainFilterOptions = [
    { value: 'all', label: 'All Domains' },
    { value: 'primary', label: 'Primary Domains' },
    { value: 'third', label: 'Third-Party Domains' },
    ...(availableDomains || []).map(domain => ({
      value: `domain:${domain}`,
      label: `@${String(domain).replace(/^@+/, '')}`
    }))
  ];

  // URL type filter options (specific values come from URL findings only)
  const urlFilterOptions = [
    { value: 'all', label: 'All URLs' },
    { value: 'employee', label: 'Employee URLs' },
    { value: 'user', label: 'User URLs' },
    ...(availableUrls || []).map(url => ({
      value: `domain:${url}`,
      label: url
    }))
  ];

  // Handle domain filter change
  const handleDomainFilterChange = (value) => {
    if (value === 'all' || value === 'primary' || value === 'third') {
      onDomainTypeChange(value);
      onDomainChange('');
    } else if (value.startsWith('domain:')) {
      const domain = value.replace('domain:', '');
      onDomainTypeChange('all');
      onDomainChange(domain);
    }
  };

  // Handle URL filter change
  const handleUrlFilterChange = (value) => {
    if (value === 'all' || value === 'employee' || value === 'user') {
      onUrlTypeChange(value);
      onDomainChange('');
    } else if (value.startsWith('url:')) {
      // deprecated path; keep for safety
      const url = value.replace('url:', '');
      onUrlTypeChange('all');
      onDomainChange(url);
    } else if (value.startsWith('domain:')) {
      const domain = value.replace('domain:', '');
      onUrlTypeChange('all');
      onDomainChange(domain);
    }
  };

  // Get current domain filter value
  const getCurrentDomainFilterValue = () => {
    if (selectedDomain) {
      return `domain:${selectedDomain}`;
    }
    return domainTypeFilter;
  };

  // Get current URL filter value
  const getCurrentUrlFilterValue = () => {
    if (selectedDomain) {
      return `domain:${selectedDomain}`;
    }
    return urlTypeFilter;
  };

  return (
    <>
      {/* Overlay */}
      <div
        className={`dashboard-sidebar-overlay ${isOpen ? 'active' : ''}`}
        onClick={handleOverlayClick}
      />

      {/* Toggle Button */}
      <button
        onClick={onToggle}
        className={`dashboard-sidebar-toggle ${isOpen ? 'open' : ''}`}
        style={{
          position: 'fixed',
          right: isOpen ? '285px' : '20px',
          top: '5%',
          transform: 'translateY(-50%)',
          zIndex: 1001,
          background: 'var(--primary-color)',
          border: 'none',
          borderRadius: '50%',
          width: '30px',
          height: '30px',
          cursor: 'pointer',
          boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
          transition: 'all 0.3s ease',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: 'var(--secondary-color)',
          fontSize: '12px'
        }}
        title={isOpen ? 'Close Filters' : 'Open Filters'}
      >
        <i className={`fas fa-${isOpen ? 'times' : 'filter'}`}></i>
      </button>

      {/* Sidebar */}
      <div
        className="dashboard-sidebar"
        style={{
          position: 'fixed',
          right: isOpen ? '0' : '-300px',
          top: 0,
          width: '300px',
          height: '100vh',
          background: 'var(--white)',
          boxShadow: isOpen ? '-4px 0 12px rgba(0,0,0,0.3)' : 'none',
          transition: 'right 0.3s ease',
          zIndex: 1000,
          padding: '20px',
          overflowY: 'auto'
        }}
      >
        <div style={{ marginBottom: '30px' }}>
          <h3 style={{
            fontSize: '18px',
            fontWeight: '600',
            color: 'var(--text)',
            marginBottom: '20px',
            display: 'flex',
            alignItems: 'center',
            gap: '10px'
          }}>
            <i className="fas fa-filter" style={{ color: 'var(--primary-color)' }}></i>
            Dashboard Filters
          </h3>

          {/* Date Range Filter */}
          <div style={{ marginBottom: '25px' }}>
            <label style={{
              display: 'block',
              fontSize: '14px',
              fontWeight: '500',
              color: 'var(--text)',
              marginBottom: '12px'
            }}>
              Date Range
            </label>
            <select
              value={selectedDateRange}
              onChange={(e) => onDateRangeChange(parseInt(e.target.value))}
              style={{
                width: '100%',
                padding: '10px 12px',
                border: '1px solid var(--border)',
                borderRadius: '6px',
                fontSize: '14px',
                background: 'var(--white)',
                color: 'var(--text)',
                cursor: 'pointer'
              }}
            >
              {dateRangeOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>

          {/* Record Type Filter */}
          <div style={{ marginBottom: '25px' }}>
            <label style={{
              display: 'block',
              fontSize: '14px',
              fontWeight: '500',
              color: 'var(--text)',
              marginBottom: '12px'
            }}>
              Record Type
            </label>
            <select
              value={recordTypeFilter}
              onChange={(e) => onRecordTypeChange(e.target.value)}
              style={{
                width: '100%',
                padding: '10px 12px',
                border: '1px solid var(--border)',
                borderRadius: '6px',
                fontSize: '14px',
                background: 'var(--white)',
                color: 'var(--text)',
                cursor: 'pointer'
              }}
            >
              <option value="all">All</option>
              <option value="domains">Domains</option>
              <option value="urls">URLs</option>
            </select>
          </div>

          {/* Conditional Domain/URL Type Filter */}
          {recordTypeFilter === 'domains' && (
            <div style={{ marginBottom: '25px' }}>
              <label style={{
                display: 'block',
                fontSize: '14px',
                fontWeight: '500',
                color: 'var(--text)',
                marginBottom: '12px'
              }}>
                Domain Type Filter
              </label>
              <select
                value={getCurrentDomainFilterValue()}
                onChange={(e) => handleDomainFilterChange(e.target.value)}
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  border: '1px solid var(--border)',
                  borderRadius: '6px',
                  fontSize: '14px',
                  background: 'var(--white)',
                  color: 'var(--text)',
                  cursor: 'pointer'
                }}
              >
                {domainFilterOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
          )}

          {recordTypeFilter === 'urls' && (
            <div style={{ marginBottom: '25px' }}>
              <label style={{
                display: 'block',
                fontSize: '14px',
                fontWeight: '500',
                color: 'var(--text)',
                marginBottom: '12px'
              }}>
                URL Type Filter
              </label>
              <select
                value={getCurrentUrlFilterValue()}
                onChange={(e) => handleUrlFilterChange(e.target.value)}
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  border: '1px solid var(--border)',
                  borderRadius: '6px',
                  fontSize: '14px',
                  background: 'var(--white)',
                  color: 'var(--text)',
                  cursor: 'pointer'
                }}
              >
                {urlFilterOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Info Section */}
          <div style={{
            background: 'rgba(255, 204, 0, 0.1)',
            border: '1px solid rgba(255, 204, 0, 0.3)',
            borderRadius: '8px',
            padding: '15px',
            marginTop: '20px'
          }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              marginBottom: '8px'
            }}>
              <i className="fas fa-info-circle" style={{ color: 'var(--primary-color)' }}></i>
              <span style={{ fontSize: '14px', fontWeight: '600', color: 'var(--text)' }}>
                Filter Info
              </span>
            </div>
            <p style={{
              fontSize: '13px',
              color: 'var(--text-light)',
              lineHeight: '1.4',
              margin: 0
            }}>
              Use these filters to customize the dashboard data.<br />
              Date range affects all charts and tables.<br />
              Record type helps focus on findings from domains or URLs.<br />
              Domain filter helps focus on specific domain types or individual domains.<br />
              URL filter helps focus on specific URL types or individual URLs.<br />
              If a domain/URL does not appear in the options, it has no findings yet.<br />
              <br /><i>Note: All records shown in this dashboard are from information stealer sources. Other records from other sources can be found in the domain/url specific pages.</i><br />
            </p>
          </div>
        </div>
      </div>
    </>
  );
};

export default DashboardSidebar; 